import React from 'react';

interface TileCoordinate {
    x: number;
    y: number;
}

interface StitchedTileResult {
    canvas: HTMLCanvasElement;
    bounds: {
        minX: number;
        minY: number;
        width: number;
        height: number;
    };
    dataUrl: string;
}

/**
 * Stitches multiple tiles together into a single canvas/image
 * @param coordinates Array of [x, y] tile coordinates
 * @param texture The texture file path to use for all tiles
 * @param tileSize Size of each tile in pixels (default 32)
 * @returns Promise that resolves to canvas, bounds, and data URL
 */
export async function stitchTiles(
    coordinates: [number, number][],
    texture: string,
    tileSize: number = 32
): Promise<StitchedTileResult> {
    if (coordinates.length === 0) {
        throw new Error('No coordinates provided for tile stitching');
    }

    // Calculate bounding box
    const xs = coordinates.map(([x]) => x);
    const ys = coordinates.map(([, y]) => y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    
    const width = maxX - minX + tileSize;
    const height = maxY - minY + tileSize;

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
        throw new Error('Failed to get 2D context from canvas');
    }

    // Load the texture image
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Failed to load texture: ${texture}`));
        img.src = texture;
    });

    // Draw each tile onto the canvas
    coordinates.forEach(([x, y]) => {
        const canvasX = x - minX;
        const canvasY = y - minY;
        ctx.drawImage(img, canvasX, canvasY, tileSize, tileSize);
    });

    return {
        canvas,
        bounds: {
            minX,
            minY,
            width,
            height
        },
        dataUrl: canvas.toDataURL()
    };
}

/**
 * Cache for stitched tiles to avoid regenerating identical tile groups
 */
const stitchedTileCache = new Map<string, StitchedTileResult>();

/**
 * Generates a cache key for a set of tile coordinates and texture
 */
function generateCacheKey(coordinates: [number, number][], texture: string): string {
    // Sort coordinates to ensure consistent cache key regardless of order
    const sortedCoords = [...coordinates].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    return `${texture}:${sortedCoords.map(([x, y]) => `${x},${y}`).join('|')}`;
}

/**
 * Cached version of stitchTiles that reuses previously stitched tiles
 */
export async function stitchTilesCached(
    coordinates: [number, number][],
    texture: string,
    tileSize: number = 32
): Promise<StitchedTileResult> {
    const cacheKey = generateCacheKey(coordinates, texture);
    
    if (stitchedTileCache.has(cacheKey)) {
        return stitchedTileCache.get(cacheKey)!;
    }

    const result = await stitchTiles(coordinates, texture, tileSize);
    stitchedTileCache.set(cacheKey, result);
    
    return result;
}

/**
 * Clears the tile stitching cache
 */
export function clearStitchedTileCache(): void {
    stitchedTileCache.clear();
}

/**
 * React hook for stitching tiles with automatic caching and cleanup
 */
export function useStitchedTiles(
    coordinates: [number, number][],
    texture: string,
    tileSize: number = 32
): {
    stitchedTile: StitchedTileResult | null;
    isLoading: boolean;
    error: string | null;
} {
    const [stitchedTile, setStitchedTile] = React.useState<StitchedTileResult | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (coordinates.length === 0) {
            setStitchedTile(null);
            setIsLoading(false);
            setError(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        stitchTilesCached(coordinates, texture, tileSize)
            .then(result => {
                setStitchedTile(result);
                setIsLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setIsLoading(false);
                setStitchedTile(null);
            });
    }, [coordinates, texture, tileSize]);

    return { stitchedTile, isLoading, error };
}